import { useCallback, useEffect, useRef, useState } from "react";
import { Settings } from "lucide-react";
import {
  fetchMultiCityWeather,
  loadSelectedWeatherCities,
  saveSelectedWeatherCityIds,
  WEATHER_CITY_CATALOG,
  MAX_WEATHER_CITIES,
  type MultiCityWeatherSnapshot,
  type WeatherCityDef,
} from "../../../services/weatherService";
import styles from "./StatusCards.module.css";

interface WeatherStatusCardProps {
  className?: string;
}

const COUNTRY_DOT: Record<string, string> = {
  KR: styles.dotOperational,
  CN: styles.dotMaintenance,
  JP: styles.dotIdle,
  SG: styles.dotIdle,
  VN: styles.dotOperational,
  US: styles.fxUsd,
  DE: styles.fxJpy,
};

export function WeatherStatusCard({ className }: WeatherStatusCardProps) {
  const [selected, setSelected] = useState<WeatherCityDef[]>(() =>
    loadSelectedWeatherCities()
  );
  const [weather, setWeather] = useState<MultiCityWeatherSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const loadWeather = useCallback(async (cities: WeatherCityDef[]) => {
    setLoading(true);
    try {
      const snap = await fetchMultiCityWeather(cities);
      setWeather(snap);
      setError(null);
    } catch {
      setError("날씨를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadWeather(selected);
  }, [selected, loadWeather]);

  useEffect(() => {
    if (!settingsOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setSettingsOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSettingsOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [settingsOpen]);

  const toggleCity = (city: WeatherCityDef) => {
    setSelected((prev) => {
      const exists = prev.some((c) => c.id === city.id);
      let next: WeatherCityDef[];
      if (exists) {
        if (prev.length <= 1) return prev; // 최소 1개
        next = prev.filter((c) => c.id !== city.id);
      } else {
        if (prev.length >= MAX_WEATHER_CITIES) return prev;
        next = [...prev, city];
      }
      saveSelectedWeatherCityIds(next.map((c) => c.id));
      return next;
    });
  };

  const titleExtra =
    selected.length > 0
      ? selected.map((c) => c.country).join(" · ")
      : "—";

  return (
    <section
      className={[styles.card, className].filter(Boolean).join(" ")}
      aria-label="도시 날씨"
    >
      <div className={styles.cardTitleRow}>
        <h2 className={styles.title}>Weather · {titleExtra}</h2>
        <div className={styles.settingsWrap} ref={panelRef}>
          <button
            type="button"
            className={styles.settingsBtn}
            aria-label="날씨 도시 설정"
            aria-expanded={settingsOpen}
            aria-haspopup="dialog"
            onClick={() => setSettingsOpen((v) => !v)}
          >
            <Settings size={15} strokeWidth={1.75} aria-hidden />
          </button>

          {settingsOpen && (
            <div
              className={styles.settingsPanel}
              role="dialog"
              aria-label="표시할 도시 선택"
            >
              <p className={styles.settingsHint}>
                최대 {MAX_WEATHER_CITIES}개 도시 (최소 1개)
              </p>
              <ul className={styles.settingsList}>
                {WEATHER_CITY_CATALOG.map((city) => {
                  const checked = selected.some((c) => c.id === city.id);
                  const disableAdd =
                    !checked && selected.length >= MAX_WEATHER_CITIES;
                  const disableRemove = checked && selected.length <= 1;
                  return (
                    <li key={city.id}>
                      <label
                        className={[
                          styles.settingsItem,
                          disableAdd || disableRemove
                            ? styles.settingsItemDisabled
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disableAdd || disableRemove}
                          onChange={() => toggleCity(city)}
                        />
                        <span>
                          {city.name}
                          <span className={styles.settingsCountry}>
                            {" "}
                            ({city.country})
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>

      {loading && !weather ? (
        <p className={styles.widgetHint}>날씨 불러오는 중…</p>
      ) : error && !weather ? (
        <p className={styles.widgetHint}>{error}</p>
      ) : weather ? (
        <ul className={styles.lineList}>
          {weather.cities.map((city) => (
            <li key={city.id} className={styles.lineItem}>
              <span className={styles.lineName}>
                <span
                  className={[
                    styles.dot,
                    COUNTRY_DOT[city.country] ?? styles.dotIdle,
                  ].join(" ")}
                  aria-hidden
                />
                {city.name}
              </span>
              <span className={styles.weatherRight}>
                <span className={styles.metricValueLite}>
                  {city.temperatureC.toFixed(1)}°C
                </span>
                <span className={styles.weatherSummary}>{city.summary}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
